/**
 * Monthly Usage Widget — shown to paid-tier users (Pro/Business/Enterprise/Ultimate).
 * Renders nothing for trial users (the TrialUsageWidget covers that case).
 *
 * Surfaces queries used / image generations used vs the monthly cap, with a
 * color-coded bar (green / amber / red) and a soft upgrade nudge once the bar
 * crosses 80%. Admin / early-adopter accounts see an "Unlimited" badge instead.
 *
 * © 2026 A-1 Affiliate Marketing LLC
 */

import React from 'react';
import './MonthlyUsageWidget.css';

const fmt = (n) => (typeof n === 'number' ? n.toLocaleString() : '0');

const Bar = ({ label, used, limit, unlimited, testid }) => {
  if (unlimited) {
    return (
      <div className="mu-row" data-testid={`${testid}-unlimited`}>
        <div className="mu-row-head">
          <span className="mu-label">{label}</span>
          <span className="mu-count mu-unlimited">Unlimited</span>
        </div>
        <div className="mu-bar mu-bar-unlimited" aria-hidden="true">
          <div className="mu-fill" style={{ width: '100%' }} />
        </div>
      </div>
    );
  }
  const pct = limit > 0 ? Math.min(100, Math.round((used / limit) * 100)) : 0;
  const state = pct >= 95 ? 'critical' : pct >= 80 ? 'warning' : 'ok';
  return (
    <div className="mu-row" data-testid={testid}>
      <div className="mu-row-head">
        <span className="mu-label">{label}</span>
        <span className="mu-count" data-testid={`${testid}-count`}>
          {fmt(used)} / {fmt(limit)}
        </span>
      </div>
      <div
        className={`mu-bar mu-bar-${state}`}
        role="progressbar"
        aria-valuenow={pct}
        aria-valuemin="0"
        aria-valuemax="100"
      >
        <div className="mu-fill" style={{ width: `${pct}%` }} data-testid={`${testid}-fill`} />
      </div>
    </div>
  );
};

export const MonthlyUsageWidget = ({ monthlyUsage }) => {
  // Render nothing for trial users — TrialUsageWidget handles that case.
  if (!monthlyUsage) return null;

  const {
    tier,
    queries_used = 0,
    queries_limit = 0,
    images_used = 0,
    images_limit = 0,
    unlimited = false,
    reset_at,
    predicted_tier,
    projected_monthly_queries,
  } = monthlyUsage;

  const queriesPct = queries_limit > 0 ? (queries_used / queries_limit) * 100 : 0;
  const imagesPct = images_limit > 0 ? (images_used / images_limit) * 100 : 0;
  const nearLimit = !unlimited && (queriesPct >= 80 || imagesPct >= 80);

  let resetLabel = '';
  if (reset_at) {
    try {
      const d = new Date(reset_at);
      resetLabel = d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
    } catch {
      resetLabel = '';
    }
  }

  return (
    <div
      className={`monthly-usage ${nearLimit ? 'near-limit' : ''}`}
      data-testid="monthly-usage-widget"
    >
      <div className="mu-header">
        <span className="mu-tier" data-testid="monthly-usage-tier">
          {unlimited
            ? 'UNLIMITED'
            : (tier === 'commander'
                ? 'COMMANDER'
                : (tier ? tier.toUpperCase() : 'PLAN'))}
        </span>
        <span className="mu-period">
          This month{resetLabel ? ` · resets ${resetLabel}` : ''}
        </span>
      </div>

      <Bar
        label="Council queries"
        used={queries_used}
        limit={queries_limit}
        unlimited={unlimited}
        testid="monthly-usage-queries"
      />
      <Bar
        label="Image generations"
        used={images_used}
        limit={images_limit}
        unlimited={unlimited}
        testid="monthly-usage-images"
      />

      {nearLimit && (
        <a
          href="/pricing"
          className="mu-nudge"
          data-testid="monthly-usage-upgrade"
        >
          Approaching cap — see higher-tier options →
        </a>
      )}

      {predicted_tier && (
        <a
          href="/pricing"
          className="mu-predicted"
          data-testid="monthly-usage-predicted"
        >
          At your current pace you&apos;ll need <strong>{predicted_tier.toUpperCase()}</strong>
          {projected_monthly_queries
            ? ` (projected ${projected_monthly_queries.toLocaleString()} queries / month)`
            : ''} →
        </a>
      )}
    </div>
  );
};

export default MonthlyUsageWidget;
